import { WelcomeOfferModal } from '@/components/WelcomeOfferModal';
import { useWelcomeOffer } from '@/contexts/WelcomeOfferContext';
import { usePayment } from '@/hooks/usePayment';
import { useAvailablePaymentMethods } from '@/hooks/useAvailablePaymentMethods';

// Type for scheduled offer (matching WelcomeOfferModal)
interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

export const GlobalWelcomeOfferModal = () => {
  const { 
    showWelcomeOfferModal, 
    setShowWelcomeOfferModal,
    currentWelcomeOffer,
    currentWelcomeGroup,
    hasMoreWelcomeOffers,
    dismissCurrentOffer,
  } = useWelcomeOffer();
  const { openPayment } = usePayment();
  const { coinflowOnly } = useAvailablePaymentMethods();

  const handlePurchase = async (offer: ScheduledOffer) => {
    // Close the welcome offer modal but DON'T dismiss yet
    // We'll dismiss only after successful payment
    setShowWelcomeOfferModal(false);
    
    // Open payment modal with the offer details including GC/SC amounts
    await openPayment(
      {
        id: offer.id.toString(),
        title: offer.title,
        price: offer.price,
        originalPrice: offer.original_price ?? undefined,
        gcAmount: offer.gc_amount,
        scAmount: offer.sc_amount,
        gcBonusAmount: offer.gc_bonus_amount,
        scBonusAmount: offer.sc_bonus_amount,
        isScheduledOffer: true,  // This is a scheduled offer, not a coin package
        coinflowOnly,  // Dynamic: true when only coinflow_hosted is enabled
      },
      {
        onSuccess: () => {
          // Dismiss the offer only after successful payment
          dismissCurrentOffer();
        },
        onBack: () => {
          // User clicked back button - reopen the welcome offer popup
          setShowWelcomeOfferModal(true);
        },
        // No onCancel - we don't want to reopen on Coinflow cancel/close
      }
    );
  };

  const handleClose = () => {
    // When closing, show the next offer if there is one
    dismissCurrentOffer();
  };

  return (
    <WelcomeOfferModal
      isOpen={showWelcomeOfferModal}
      onClose={handleClose}
      welcomeOffer={currentWelcomeOffer}
      welcomeGroup={currentWelcomeGroup}
      hasMoreOffers={hasMoreWelcomeOffers}
      onPurchase={handlePurchase}
    />
  );
};
